import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  registerLogoutHandler,
  registerTokenGetter,
} from "../auth/token-accessor";

export interface AuthUser {
  sub: string; // Auth0 user ID
  email: string;
  name: string;
  picture: string;
}

export interface AuthUserCredentials {
  accessToken: string;
  idTokenPayload: AuthUser;
}

const AuthContext = React.createContext<{
  auth: AuthUserCredentials | null;
}>({
  auth: null,
});

// Adapts the Auth0 React SDK into the shape the rest of the app already
// consumes via `useAuth()`. Token renewal (and refresh-token rotation) is
// handled by the SDK, so there is no manual session/localStorage handling here.
export const AuthRoutesProvider: React.FC<{ children: React.ReactNode }> = (
  props,
) => {
  const { isAuthenticated, user, getAccessTokenSilently, logout } = useAuth0();
  const [auth, setAuth] = React.useState<AuthUserCredentials | null>(null);

  // Expose a fresh-token getter to the (non-React) http client.
  React.useEffect(() => {
    registerTokenGetter(async () => {
      if (!isAuthenticated) {
        return null;
      }
      return getAccessTokenSilently();
    });
  }, [isAuthenticated, getAccessTokenSilently]);

  // Allow the http client to trigger a clean logout on an unrecoverable 401.
  React.useEffect(() => {
    registerLogoutHandler(() => {
      logout({ logoutParams: { returnTo: window.location.origin } });
    });
  }, [logout]);

  // Resolve an access token once authenticated and keep the context value in
  // the credential shape the app expects.
  React.useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !user) {
      setAuth(null);
      return;
    }

    getAccessTokenSilently()
      .then((accessToken) => {
        if (cancelled) {
          return;
        }
        setAuth({
          accessToken,
          idTokenPayload: {
            sub: user.sub ?? "",
            email: user.email ?? "",
            name: user.name ?? "",
            picture: user.picture ?? "",
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAuth(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, getAccessTokenSilently]);

  return (
    <AuthContext.Provider value={{ auth }}>
      {props.children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthUserCredentials | null => {
  const { auth } = React.useContext(AuthContext);

  return auth;
};
